import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { listEvents } from "../../Redux/Actions/eventActions";
import Loading from "../LoadingError/Loading";
import Message from "../LoadingError/Error";

const UpcomingEvents = () => {
  const dispatch = useDispatch();

  const eventList = useSelector((state) => state.eventList);
  const { loading, error, events } = eventList;
  
  const [showNotifyForm, setShowNotifyForm] = useState(false);
  const [email, setEmail] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  useEffect(() => {
    dispatch(listEvents());
  }, [dispatch]);

  const isEventPast = (eventDate) => {
    const today = new Date();
    const date = new Date(eventDate);
    return date < today;
  };

  const isEventToday = (eventDate) => {
    const today = new Date();
    const date = new Date(eventDate);
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  // Filter events into upcoming and past
  const upcomingEvents = events ? events.filter((event) => !isEventPast(event.date)) : [];
  const pastEvents = events ? events.filter((event) => isEventPast(event.date)) : [];

  // Sort upcoming events by date (closest first)
  const sortedUpcomingEvents = [...upcomingEvents].sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    const isTodayA = isEventToday(a.date);
    const isTodayB = isEventToday(b.date);
    
    // If one is today, prioritize it over other upcoming events
    if (isTodayA && !isTodayB) return -1;
    if (!isTodayA && isTodayB) return 1;
    
    // For upcoming events, sort by closest date first
    return dateA - dateB;
  });

  // Sort past events by date (most recent first)
  const sortedPastEvents = [...pastEvents].sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return dateB - dateA;
  });

  // Get latest upcoming event
  const featuredUpcomingEvent = sortedUpcomingEvents.length > 0 ? sortedUpcomingEvents[0] : null;
  
  // Get latest past event
  const latestPastEvent = sortedPastEvents.length > 0 ? sortedPastEvents[0] : null;

  const handleSubscribe = (e) => {
    e.preventDefault();
    
    // Simple validation
    if (!email || !email.includes('@') || !email.includes('.')) {
      setToastMessage("Please enter a valid email address");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      return;
    }
    
    // Here you would typically send this to your backend
    setToastMessage("Thank you! You'll be notified of new events.");
    setShowToast(true);
    setEmail("");
    setShowNotifyForm(false);
    setTimeout(() => setShowToast(false), 3000);
  };

  return (
    <div className="upcoming-events-section">
      <div className="section-title">
        <h2>Events</h2>
      </div>

      {loading ? (
        <Loading />
      ) : error ? (
        <Message variant="danger">{error}</Message>
      ) : (
        <>
          {!featuredUpcomingEvent && !latestPastEvent ? (
            <div className="no-events-container">
              <div className="no-events-content">
                <div className="no-events-icon">
                  <i className="far fa-calendar-times"></i>
                </div>
                <h3 className="no-events-title">No Events Yet</h3>
                <p className="no-events-message">We're preparing something special for you.</p>
                <div className="no-events-actions">
                  <button 
                    className="no-events-subscribe"
                    onClick={() => setShowNotifyForm(!showNotifyForm)}
                  >
                    Get Notified
                  </button>
                </div>
                
                {showNotifyForm && (
                  <div className="notification-form-container">
                    <form onSubmit={handleSubscribe} className="notification-form">
                      <input
                        type="email"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="notification-input"
                        required
                      />
                      <button type="submit" className="notification-submit">
                        Subscribe
                      </button>
                    </form>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="events-category-header">
                <h3 className="category-title upcoming">Upcoming</h3>
                <h3 className="category-title past">Recent</h3>
              </div>
              
              <div className="featured-events-grid">
                {/* Upcoming Event Card */}
                {featuredUpcomingEvent ? (
                  <EventCard 
                    event={featuredUpcomingEvent} 
                    isPast={false}
                    isToday={isEventToday(featuredUpcomingEvent.date)}
                  />
                ) : (
                  <div className="no-event-placeholder upcoming">
                    <div className="placeholder-content">
                      <i className="far fa-calendar-plus"></i>
                      <h3>No Upcoming Events</h3>
                      <p>Stay tuned for new events!</p>
                      <button 
                        className="notify-button"
                        onClick={() => setShowNotifyForm(!showNotifyForm)}
                      >
                        Get Notified
                      </button>
                      
                      {showNotifyForm && (
                        <div className="notification-form-container">
                          <form onSubmit={handleSubscribe} className="notification-form">
                            <input
                              type="email"
                              placeholder="Enter your email"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              className="notification-input"
                              required
                            />
                            <button type="submit" className="notification-submit">
                              Subscribe
                            </button>
                          </form>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Past Event Card */}
                {latestPastEvent ? (
                  <EventCard 
                    event={latestPastEvent} 
                    isPast={true}
                    isToday={false}
                  />
                ) : (
                  <div className="no-event-placeholder past">
                    <div className="placeholder-content">
                      <i className="far fa-calendar-check"></i>
                      <h3>No Past Events</h3>
                      <p>Our event history will appear here</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="view-all-events">
                <Link to="/events" className="view-all-button">
                  View All Events
                </Link>
              </div>
            </>
          )}
          
          {/* Toast Notification */}
          {showToast && (
            <div className="toast-notification">
              <div className="toast-content">
                <i className="fas fa-info-circle"></i>
                <p>{toastMessage}</p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Extracted Event Card component for reuse
const EventCard = ({ event, isPast, isToday }) => {
  const eventDate = new Date(event.date);
  
  // Format date to look like "May 15, 2025"
  const formattedDate = eventDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  
  // Format time part
  const formattedTime = eventDate.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <div className={`event-card ${isPast ? "past-event-card" : "upcoming-event-card"}`}>
      <div className="event-status">
        <span className={`event-badge ${isPast ? "past" : isToday ? "today" : "upcoming"}`}>
          {isPast ? "Past" : isToday ? "Today" : "Upcoming"}
        </span>
      </div>

      <div className="event-image-container">
        <img
          src={event.poster}
          alt={event.name}
          className="event-image"
        />
        {isPast && (
          <div className="event-overlay">
            <span>Event Ended</span>
          </div>
        )}
      </div>

      <div className="event-details">
        <h3 className="event-title">{event.name}</h3>
        
        <div className="event-info">
          <p>
            <i className="far fa-calendar-alt"></i>
            {formattedDate}
          </p>
          <p>
            <i className="far fa-clock"></i>
            {formattedTime}
          </p>
          <p>
            <i className="fas fa-map-marker-alt"></i>
            {event.location}
          </p>
        </div>

        <div className="event-footer">
          <span className="event-price">
            {Number(event.ticketPrice) === 0 ? (
              <span className="free-entry">Free Entry</span>
            ) : (
              `Ksh ${event.ticketPrice}`
            )}
          </span>

          {!isPast && (
            <a
              href={event.ticketLink}
              target="_blank"
              rel="noopener noreferrer"
              className="ticket-button"
            >
              Get Ticket
            </a>
          )}
          
          {isPast && (
            <span className="past-event-label">Event Completed</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpcomingEvents;